import React, { FC, useEffect, useMemo, useState } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';

import { IdEnums, ReactTable, SelectTypes } from '@nhl/nhl-breakaway-ui';
import { Row } from '@tanstack/react-table';

import CommonMessages from '~/core/commonMessages';
import { LeagueNames, PlayerPosition } from '~/core/enums';

import { StyledSelect } from '../../styles';
import { Player } from '../../types';
import { createShortGameTypeOption } from '../utils';

import { careerStatsColumnsForGoalies, careerStatsColumnsForSkaters } from './columns';
import { CAREER } from './constants';

import { EliteProspectsLink, LabelContainer, PlayerTableContainer, SelectContainer } from './styles';

type PlayerCareerStatsProps = {
    player: Player;
};

export const PlayerCareerStats: FC<PlayerCareerStatsProps> = ({ player }) => {
    const [selectedGameType, setSelectedGameType] = useState(IdEnums.GameType.regularSeason);
    const [selectedLeague, setSelectedLeague] = useState(LeagueNames.allLeagues);
    const isGoalie = player.position === PlayerPosition.goalie;
    const intl = useIntl();

    useEffect(() => {
        player &&
            player.seasonTotals.find((season) => {
                if (season.gameTypeId === IdEnums.GameType.regularSeason && season.leagueAbbrev === LeagueNames.nhl) {
                    setSelectedLeague(LeagueNames.nhl);
                }
            });
    }, [player]);

    const columns = isGoalie
        ? careerStatsColumnsForGoalies(selectedLeague === LeagueNames.nhl, intl)
        : careerStatsColumnsForSkaters(selectedLeague === LeagueNames.nhl, intl);

    const gameTypeOptions = useMemo(() => {
        const optionsBase = [
            createShortGameTypeOption(intl, IdEnums.GameType.regularSeason),
            createShortGameTypeOption(intl, IdEnums.GameType.playoffs),
        ];

        const hasNHLPlayoffs = player?.seasonTotals.some(
            (obj) => obj.leagueAbbrev === LeagueNames.nhl && obj.gameTypeId === IdEnums.GameType.playoffs
        );

        const hasNonNHLPlayoffs = player?.seasonTotals.some(
            (obj) => obj.leagueAbbrev !== LeagueNames.nhl && obj.gameTypeId === IdEnums.GameType.playoffs
        );

        return {
            [LeagueNames.nhl]: hasNHLPlayoffs ? optionsBase : optionsBase.slice(0, 1),
            [LeagueNames.allLeagues]: hasNonNHLPlayoffs ? optionsBase : optionsBase.slice(0, 1),
        };
    }, [intl, player]);

    const filteredData = useMemo(() => {
        return player?.seasonTotals.filter((obj) => {
            if (selectedLeague === LeagueNames.nhl) {
                return obj.leagueAbbrev === selectedLeague && obj.gameTypeId === selectedGameType;
            }
            // When "All Leagues" and "Regular Season" are selected we display all game types except for preseason and
            // playoffs. This means we can show events like 4 Nation, Olympics, and World Cups
            return selectedGameType === IdEnums.GameType.regularSeason
                ? obj.gameTypeId !== IdEnums.GameType.preseason && obj.gameTypeId !== IdEnums.GameType.playoffs
                : obj.gameTypeId === selectedGameType;
        });
    }, [selectedGameType, selectedLeague, player]);

    const leagueOptions = useMemo(() => {
        const options = [
            {
                key: 1,
                label: intl.formatMessage(CommonMessages.leagueDescriptions[LeagueNames.nhl]),
                value: LeagueNames.nhl,
            },
            {
                key: 2,
                label: intl.formatMessage(CommonMessages.leagueDescriptions[LeagueNames.allLeagues]),
                value: LeagueNames.allLeagues,
            },
        ];

        // don't show NHL league option if player has never played in NHL
        if (player?.seasonTotals.findIndex((obj) => obj.leagueAbbrev === LeagueNames.nhl) === -1) {
            options.shift();
        }

        return options;
    }, [intl, player]);

    const filteredDataWithCareerTotals = useMemo(() => {
        if (selectedLeague === LeagueNames.nhl) {
            if (filteredData.length === 0) return [];
            const careerTotals =
                selectedGameType === IdEnums.GameType.playoffs
                    ? player.careerTotals?.playoffs
                    : player.careerTotals?.regularSeason;

            filteredData.push({
                ...careerTotals,
                season: CAREER,
            });
        }

        return filteredData;
    }, [player.careerTotals, selectedGameType, selectedLeague, filteredData]);

    const ariaLabels = useMemo(
        () => ({
            gameTypeSelect: {
                dropdownButton: intl.formatMessage(CommonMessages.ariaLabels.select.gameType.dropdownButton),
                optionsBox: intl.formatMessage(CommonMessages.ariaLabels.select.gameType.optionsBox),
            },
            leagueSelect: {
                dropdownButton: intl.formatMessage({
                    defaultMessage: 'Expand League Options',
                    description:
                        'Label for select element button, prompting user to expand league options in a dropdown menu',
                }),
                optionsBox: intl.formatMessage({
                    defaultMessage: 'League Options',
                    description:
                        'Label indicating that user is viewing a list of league options for a select component',
                }),
            },
        }),
        [intl]
    );

    return (
        <>
            <LabelContainer>
                <h4 aria-level={3}>
                    <FormattedMessage {...CommonMessages.tableHeaders.long.careerStats} />
                </h4>

                <SelectContainer>
                    <StyledSelect
                        id="league-select"
                        dropdownButtonAriaLabel={ariaLabels.leagueSelect.dropdownButton}
                        onOptionChange={(_, value: SelectTypes.BasicOption) =>
                            setSelectedLeague(value.value as LeagueNames)
                        }
                        options={leagueOptions}
                        optionsBoxAriaLabel={ariaLabels.leagueSelect.optionsBox}
                        selectedOption={
                            selectedLeague
                                ? leagueOptions?.find((option) => option.value === selectedLeague)
                                : undefined
                        }
                    />
                    <StyledSelect
                        id="game-type-select"
                        dropdownButtonAriaLabel={ariaLabels.gameTypeSelect.dropdownButton}
                        onOptionChange={(_, value: SelectTypes.BasicOption) =>
                            setSelectedGameType(value.value as number)
                        }
                        options={gameTypeOptions[selectedLeague]}
                        optionsBoxAriaLabel={ariaLabels.gameTypeSelect.optionsBox}
                        selectedOption={
                            selectedGameType
                                ? gameTypeOptions[selectedLeague].find((option) => option.value === selectedGameType)
                                : undefined
                        }
                    />
                </SelectContainer>
            </LabelContainer>

            <PlayerTableContainer>
                <ReactTable
                    data={filteredDataWithCareerTotals}
                    columns={columns}
                    getRowClassName={(row: Row<Player>, index: number) =>
                        selectedLeague === LeagueNames.nhl && index === filteredDataWithCareerTotals.length - 1
                            ? 'career-totals'
                            : ''
                    }
                    tableId="career-stats-table"
                />
            </PlayerTableContainer>
            {selectedLeague !== LeagueNames.nhl && (
                <EliteProspectsLink>
                    <FormattedMessage
                        defaultMessage="Non-NHL statistics provided by"
                        description="A phrase describing who provides non-NHL statistics"
                    />
                    &nbsp;
                    <a href="https://www.eliteprospects.com/" target="_blank" rel="noreferrer">
                        EliteProspects.com
                    </a>
                </EliteProspectsLink>
            )}
        </>
    );
};
